import React, { useEffect, useState } from 'react';
import { ReactComponent as LeftArrow } from 'icons/left-arrow.svg';
import ReactDOM from 'react-dom';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { checkEmptyObject } from 'utils/functionality';
import { getDetailOrder } from 'features/orders/detailOrderSlice';
import WithoutDriverDetail from './without-driver-detail';
import WithDriverDetail from './with-driver-detail';
import PreviewImage from 'components/Modals/PreviewImage';
import { getAllApprovalData, getApprovalDataById, updateApprovalStatus } from 'features/approvals/actions';
import { fetchOrderById, fetchOrderByIdTaskAdmin } from 'features/orders/actions';
import { useAppContext } from 'components/Context/AppContext';
import AirportTransferDetail from './airport-transfer-detail';
import DriverDetail from './driver-detail';
import { useFliptBoolean } from '@flipt-io/flipt-client-react';
import { getFileName } from 'utils/getFileName';
import { getDetailDriver } from 'features/drivers/actions';
import { trackEvent } from 'utils/analytics';

const INITIAL_STATE = {
  booking_price: 0,
  order_detail: {
    end_booking_date: '',
    end_booking_time: '',
    rental_delivery_location: '',
    rental_delivery_location_detail: '',
    rental_return_location: '',
    rental_return_location_detail: '',
    start_booking_date: '',
    start_booking_time: '',
    vehicle_id: 0,
    location_id: 0,
    loc_time_id: '',
    landing_time: '',
    flight_number: '',
    booking_zones: [],
  },
  order_type_id: 0,
  deposit: 0,
  over_time_price: 0,
  over_time: 0,
  order_violations: [],
};

const TaskAdminDetail = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const orderData = useSelector(getDetailOrder);
  const approvalData = useSelector((state) => state.approvals.selected);
  const user = useSelector((state) => state.user.profile);

  const isDriverDeleteApproval = useFliptBoolean('FLAG_FE_DELETE_DRIVER_APPROVAL', false, user.id || 'anonymous');

  const [oldOrderData, setOldOrderData] = useState({});
  const [newOrderData, setNewOrderData] = useState(INITIAL_STATE);
  const [showImage, setShowImage] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');

  const { setShowConfirmation, showToast, setShowSpinner } = useAppContext();

  const { slug } = useParams();

  const [searchParams] = useSearchParams();

  const approvalId = searchParams.get('id');
  const approvalType = searchParams.get('type');

  useEffect(() => {
    if (slug && approvalId) {
      dispatch(getApprovalDataById(approvalId));
    }
  }, [slug, approvalId]);

  useEffect(() => {
    if (!slug) return;
    if (checkEmptyObject(approvalData)) return;
    if (isDriverDeleteApproval) {
      if (approvalType === 'order') {
        if (approvalData.status === 'finished' || approvalData.status === 'rejected') {
          dispatch(fetchOrderByIdTaskAdmin({ transaction_key: slug, approval_id: approvalData.id }));
        } else {
          dispatch(fetchOrderById(slug));
        }
      } else if (approvalType === 'user') {
        dispatch(getDetailDriver(slug));
      }
      return;
    }

    if (approvalData.status === 'finished' || approvalData.status === 'rejected') {
      dispatch(fetchOrderByIdTaskAdmin({ transaction_key: slug, approval_id: approvalData.id }));
    } else {
      dispatch(fetchOrderById(slug));
    }
  }, [slug, approvalData, isDriverDeleteApproval, approvalType]);

  useEffect(() => {
    if (checkEmptyObject(orderData) || checkEmptyObject(approvalData)) return;
    setOldOrderData(orderData || INITIAL_STATE);
    setNewOrderData(
      (approvalData.approval_setting.name.includes('delete order')
        ? { ...approvalData, data: orderData }
        : approvalData) || INITIAL_STATE,
    );
  }, [orderData, approvalData]);

  const backHandler = () => {
    navigate(-1);
  };

  const imagePreviewHandler = (image) => {
    setShowImage(true);
    setSelectedImage(image);
  };

  const getApprovalMethod = (methodName) => {
    if (methodName.includes('update order')) return 'update data order';
    else if (methodName.includes('delete order')) return 'delete data order';
    else if (methodName.includes('delete driver')) return 'delete data driver';
  };

  const getApprovalStatus = (status) => {
    if (status === 'processed') return 'proses';
    else if (status === 'approved') return 'menyetujui';
    else return 'menolak';
  };

  const confirmHandler = (status) => {
    const method = getApprovalMethod(approvalData?.approval_setting?.name);
    const approvalStatus = getApprovalStatus(status);

    setShowConfirmation({
      message: `Apakah anda yakin ingin ${approvalStatus} ${method} ini?`,
      show: true,
      onClick: async () => {
        setShowSpinner(true);
        try {
          trackEvent('approval_decided', {
            approval_id: +approvalId,
            decision: status,
            source: 'task_admin_detail',
          });
          await dispatch(updateApprovalStatus({ status, id: +approvalId })).unwrap();
          showToast({ type: 'success', message: 'Data Berhasil Disetujui' });
        } catch (err) {
          showToast({ type: 'error', message: 'Data Gagal Disetujui' + `[${err.message}]` });
        } finally {
          setShowSpinner(false);
          navigate(-1);
          dispatch(
            getAllApprovalData({
              page: 1,
              limit: 10,
            }),
          );
        }
      },
    });
  };

  const orderTypeView = () => {
    if (orderData?.order_type_id === 2) {
      return (
        <AirportTransferDetail
          getFileName={getFileName}
          imagePreviewHandler={imagePreviewHandler}
          newOrderData={newOrderData}
          oldOrderData={oldOrderData}
          orderData={orderData}
          transactionKey={slug}
          confirmHandler={confirmHandler}
        />
      );
    } else if (orderData?.order_detail?.without_driver) {
      return (
        <WithoutDriverDetail
          getFileName={getFileName}
          imagePreviewHandler={imagePreviewHandler}
          newOrderData={newOrderData}
          oldOrderData={oldOrderData}
          orderData={orderData}
          transactionKey={slug}
          confirmHandler={confirmHandler}
        />
      );
    } else {
      return (
        <WithDriverDetail
          getFileName={getFileName}
          imagePreviewHandler={imagePreviewHandler}
          newOrderData={newOrderData}
          oldOrderData={oldOrderData}
          orderData={orderData}
          transactionKey={slug}
          confirmHandler={confirmHandler}
        />
      );
    }
  };

  const detailFormView = () => {
    if (isDriverDeleteApproval) {
      if (approvalType === 'order') return orderTypeView();
      else if (approvalType === 'user') return <DriverDetail confirmHandler={confirmHandler} />;
      return;
    }

    return orderTypeView();
  };

  return (
    <>
      <div className="task-admin">
        <button className="task-admin-detail__back-btn" onClick={backHandler}>
          <LeftArrow />
          <p>Kembali</p>
        </button>
        {detailFormView()}
      </div>
      {showImage &&
        ReactDOM.createPortal(
          <PreviewImage image={selectedImage} setShow={() => setShowImage(false)} />,
          document.getElementById('modal'),
        )}
    </>
  );
};

export default TaskAdminDetail;
